import {
  AppointmentDevice,
  DeviceCategory,
  DeviceConfig,
  DeviceGoNoGo,
  DeviceWave,
  ElectrodeState,
  StaffDevice,
} from "../services/types/device.types";

export const ELECTRODE_SITES = [
  "Fp1", "Fp2", "F7", "F3", "Fz", "F4", "F8",
  "T3", "C3", "Cz", "C4", "T4", "T5", "P3", "Pz", "P4", "T6",
  "O1", "Oz", "O2", "A1", "A2",
] as const;

export const WAVE_BANDS = [
  { key: "delta", labelKey: "DEVICE_WAVE_DELTA" },
  { key: "theta", labelKey: "DEVICE_WAVE_THETA" },
  { key: "alpha", labelKey: "DEVICE_WAVE_ALPHA" },
  { key: "beta", labelKey: "DEVICE_WAVE_BETA" },
  { key: "gamma", labelKey: "DEVICE_WAVE_GAMMA" },
] as const;

export const DEVICE_CATEGORIES: { value: DeviceCategory; labelKey: string }[] = [
  { value: "nf", labelKey: "DEVICE_CATEGORY_NF" },
  { value: "tdcs", labelKey: "DEVICE_CATEGORY_TDCS" },
  { value: "loreta", labelKey: "DEVICE_CATEGORY_LORETA" },
  { value: "scan", labelKey: "DEVICE_CATEGORY_SCAN" },
];

export interface DevicePanelConfig {
  electrodes: boolean;
  electrodeMode: "active" | "polarity";
  waves: boolean;
  gonogo: boolean;
}

export const DEVICE_PANEL_CONFIG: Record<DeviceCategory, DevicePanelConfig> = {
  nf: { electrodes: true, electrodeMode: "active", waves: true, gonogo: true },
  tdcs: { electrodes: true, electrodeMode: "polarity", waves: false, gonogo: false },
  loreta: { electrodes: true, electrodeMode: "active", waves: true, gonogo: false },
  scan: { electrodes: true, electrodeMode: "active", waves: false, gonogo: false },
};

export function getDefaultElectrodes(): Record<string, ElectrodeState> {
  return ELECTRODE_SITES.reduce<Record<string, ElectrodeState>>((acc, site) => {
    acc[site] = "unused";
    return acc;
  }, {});
}

export function getDefaultWaves(): DeviceWave[] {
  return WAVE_BANDS.map((w) => ({
    band: w.key,
    enabled: false,
    direction: "up",
    threshold: undefined,
  }));
}

export function getDefaultRegistrationConfig(): DeviceConfig {
  return { capabilities: [] };
}

/** Resolve capabilities from registration config (supports legacy single category). */
export function getDeviceCapabilities(raw?: DeviceConfig | null): DeviceCategory[] {
  if (raw?.capabilities?.length) return raw.capabilities;
  if (raw?.category) return [raw.category];
  return [];
}

export function normalizeRegistrationConfig(raw?: DeviceConfig | null): DeviceConfig {
  return { capabilities: getDeviceCapabilities(raw) };
}

export function formatCapabilitiesLabel(caps: DeviceCategory[]): string {
  return caps
    .map((c) => DEVICE_CATEGORIES.find((d) => d.value === c)?.value.toUpperCase() ?? c)
    .join(" + ");
}

export function getDefaultDeviceConfig(category: DeviceCategory = "nf"): DeviceConfig {
  return {
    category,
    electrodes: getDefaultElectrodes(),
    waves: getDefaultWaves(),
    goNoGo: { enabled: false, goCount: 20, stopCount: 10 },
    durationMinutes: 20,
  };
}

export function cycleElectrodeState(
  current: ElectrodeState,
  mode: "active" | "polarity",
): ElectrodeState {
  const seq: ElectrodeState[] =
    mode === "polarity" ? ["unused", "anode", "cathode"] : ["unused", "active"];
  const idx = seq.indexOf(current);
  return seq[(idx + 1) % seq.length];
}

/** Smart polarity click: first unused site -> anode, second -> cathode, then cycle per site. */
export function applyPolarityElectrodeClick(
  electrodes: Record<string, ElectrodeState>,
  site: string,
): Record<string, ElectrodeState> {
  const current = electrodes[site] ?? "unused";

  if (current !== "unused") {
    return { ...electrodes, [site]: cycleElectrodeState(current, "polarity") };
  }

  const hasAnode = Object.entries(electrodes).some(([s, state]) => s !== site && state === "anode");
  const hasCathode = Object.entries(electrodes).some(([s, state]) => s !== site && state === "cathode");

  if (!hasAnode) return { ...electrodes, [site]: "anode" };
  if (!hasCathode) return { ...electrodes, [site]: "cathode" };
  return { ...electrodes, [site]: "anode" };
}

export function getPolarityElectrodeSummary(
  electrodes?: Record<string, ElectrodeState>,
): { anodeSites: string[]; cathodeSites: string[] } {
  const anodeSites: string[] = [];
  const cathodeSites: string[] = [];

  Object.entries(electrodes ?? {}).forEach(([site, state]) => {
    if (state === "anode") anodeSites.push(site);
    if (state === "cathode") cathodeSites.push(site);
  });

  return { anodeSites, cathodeSites };
}

export function normalizeDeviceConfig(raw?: DeviceConfig | null): DeviceConfig {
  const category = raw?.category ?? "nf";
  const defaults = getDefaultDeviceConfig(category);
  return {
    category,
    electrodes: { ...defaults.electrodes, ...(raw?.electrodes ?? {}) },
    waves: defaults.waves?.map((w) => {
      const existing = raw?.waves?.find((x) => x.band === w.band);
      return existing ? { ...w, ...existing } : w;
    }),
    goNoGo: { ...defaults.goNoGo, ...(raw?.goNoGo ?? {}) } as DeviceGoNoGo,
    durationMinutes: raw?.durationMinutes ?? defaults.durationMinutes,
  };
}

export function isDeviceContact(contact: {
  deviceConfig?: DeviceConfig | null;
  relation?: string;
  roleId?: number;
}): boolean {
  return contact.roleId === 7 || Boolean(contact.deviceConfig) || contact.relation === "device";
}

export function staffDeviceToAppointmentDevice(
  device: StaffDevice,
  sessionConfig: DeviceConfig,
): AppointmentDevice {
  return {
    deviceContactId: device.deviceContactId,
    deviceContactGuid: device.deviceContactGuid,
    deviceName: device.deviceName,
    category: sessionConfig.category,
    electrodes: sessionConfig.electrodes,
    waves: sessionConfig.waves,
    goNoGo: sessionConfig.goNoGo,
    durationMinutes: sessionConfig.durationMinutes,
  };
}

export function validateDeviceConfig(
  config: DeviceConfig,
  options: { requireSessionWaves?: boolean } = {},
): string | null {
  const category = config.category;
  if (!category) return "DEVICE_CATEGORY_REQUIRED";

  const panel = DEVICE_PANEL_CONFIG[category];
  const electrodes = config.electrodes ?? {};
  const hasActiveElectrode = Object.values(electrodes).some((s) => s !== "unused");

  if (panel.electrodes && !hasActiveElectrode) {
    return "DEVICE_ELECTRODE_REQUIRED";
  }

  if (panel.electrodes && panel.electrodeMode === "polarity") {
    const hasAnode = Object.values(electrodes).some((s) => s === "anode");
    const hasCathode = Object.values(electrodes).some((s) => s === "cathode");
    if (!hasAnode || !hasCathode) return "DEVICE_POLARITY_REQUIRED";
  }

  if (options.requireSessionWaves && panel.waves) {
    const enabledWaves = (config.waves ?? []).filter((w) => w.enabled);
    if (enabledWaves.length === 0) return "DEVICE_WAVE_REQUIRED";
  }

  if (!config.durationMinutes || config.durationMinutes < 1) {
    return "DEVICE_DURATION_REQUIRED";
  }

  return null;
}

export function getDeviceSummaryLabel(device?: AppointmentDevice | DeviceConfig | null): string {
  if (!device) return "";
  const name = "deviceName" in device ? (device as AppointmentDevice).deviceName : "";
  const caps = getDeviceCapabilities(device);
  if (device.category) {
    const modality = device.category.toUpperCase();
    return name ? `${name} — ${modality}` : modality;
  }
  if (caps.length) {
    const capLabel = formatCapabilitiesLabel(caps);
    return name ? `${name} (${capLabel})` : capLabel;
  }
  return name;
}

export function getActiveElectrodeLabels(electrodes?: Record<string, ElectrodeState>): string[] {
  if (!electrodes) return [];
  return Object.entries(electrodes)
    .filter(([, state]) => state !== "unused")
    .map(([site, state]) => `${site}:${state}`);
}
